import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import {
  purchaseCredits,
  PRODUCT_CODES,
  calculateTotal,
} from '../api/payments'
import './Pricing.css'

/**
 * Pricing plans for Pay-Per-Plan model
 */
const pricingPlans = [
  {
    productCode: PRODUCT_CODES.singleFamily,
    name: 'Single-Family Plan',
    price: '$45',
    period: '/plan',
    description: 'For single-family homes with 1 dwelling unit',
    features: [
      'AI-powered insulation analysis',
      'Complete takeoff report',
      'PDF export included',
      'Never expires',
    ],
    cta: 'Buy Credits',
    ctaVariant: 'primary' as const,
    featured: true,
  },
  {
    productCode: PRODUCT_CODES.multiUnit,
    name: 'Multi-Unit Plan',
    price: '$75',
    period: '/plan',
    description: 'For buildings with 2+ dwelling units',
    features: [
      'AI-powered insulation analysis',
      'Complete takeoff report',
      'PDF export included',
      'Never expires',
    ],
    cta: 'Buy Credits',
    ctaVariant: 'secondary' as const,
    featured: false,
  },
]

/**
 * Credit bundle options for bulk purchases
 */
const bundleOptions = [
  { quantity: 1, label: '1 credit' },
  { quantity: 5, label: '5 credits' },
  { quantity: 10, label: '10 credits' },
  { quantity: 25, label: '25 credits' },
]

export default function Pricing() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loadingProduct, setLoadingProduct] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [quantities, setQuantities] = useState<Record<string, number>>({
    [PRODUCT_CODES.singleFamily]: 5,
    [PRODUCT_CODES.multiUnit]: 5,
  })

  const handleQuantityChange = (productCode: string, quantity: number) => {
    setQuantities((prev) => ({ ...prev, [productCode]: quantity }))
  }

  const handlePurchase = async (productCode: string) => {
    setError(null)

    // If not logged in, redirect to login with return URL
    if (!user) {
      navigate('/login', { state: { returnTo: '/pricing', selectedProduct: productCode } })
      return
    }

    setLoadingProduct(productCode)

    const quantity = quantities[productCode] || 1

    const response = await purchaseCredits({
      productCode,
      quantity,
      successUrl: `${window.location.origin}/purchase/success`,
      cancelUrl: `${window.location.origin}/pricing`,
    })

    setLoadingProduct(null)

    if (response.success && response.data) {
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl
    } else {
      setError(response.error || 'Failed to start checkout. Please try again.')
    }
  }

  return (
    <>
      {/* Hero */}
      <section className="pricing-hero">
        <div className="container">
          <h1 className="pricing-hero-title">Simple Pay-Per-Plan Pricing</h1>
          <p className="pricing-hero-subtitle">
            No subscriptions. No monthly fees. Just pay for the plans you process.
            Credits never expire.
          </p>
        </div>
      </section>

      {/* Error Alert */}
      {error && (
        <div className="container">
          <div className="pricing-error">{error}</div>
        </div>
      )}

      {/* Pricing Cards */}
      <section className="pricing">
        <div className="container">
          <div className="pricing-grid pricing-grid-2">
            {pricingPlans.map((plan) => {
              const quantity = quantities[plan.productCode] || 1
              const total = calculateTotal(plan.productCode, quantity)

              return (
                <div
                  key={plan.productCode}
                  className={`pricing-card ${plan.featured ? 'featured' : ''}`}
                >
                  {plan.featured && (
                    <span className="pricing-badge">Most Popular</span>
                  )}
                  <h3 className="pricing-name">{plan.name}</h3>
                  <div className="pricing-price">
                    {plan.price}
                    <span>{plan.period}</span>
                  </div>
                  <p className="pricing-description">{plan.description}</p>

                  {/* Quantity Selector */}
                  <div className="pricing-quantity">
                    <label htmlFor={`quantity-${plan.productCode}`}>
                      How many credits?
                    </label>
                    <div className="quantity-buttons">
                      {bundleOptions.map((option) => (
                        <button
                          key={option.quantity}
                          type="button"
                          className={`quantity-btn ${quantity === option.quantity ? 'active' : ''}`}
                          onClick={() => handleQuantityChange(plan.productCode, option.quantity)}
                        >
                          {option.quantity}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="pricing-total">
                    <span className="total-label">Total:</span>
                    <span className="total-amount">{total.formatted}</span>
                  </div>

                  <ul className="pricing-features">
                    {plan.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>

                  <button
                    className={`btn btn-${plan.ctaVariant} btn-large ${loadingProduct === plan.productCode ? 'loading' : ''}`}
                    onClick={() => handlePurchase(plan.productCode)}
                    disabled={loadingProduct !== null}
                  >
                    {loadingProduct === plan.productCode
                      ? 'Processing...'
                      : `${plan.cta} - ${total.formatted}`}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Offline Credits Info */}
      <section className="pricing-offline">
        <div className="container">
          <h2 className="section-title">Work Offline, Anytime</h2>
          <p className="section-subtitle">
            Purchase credits ahead of time to use PlanScan even when you're at a job
            site with no internet. Your credits sync automatically when you reconnect.
          </p>
          <div className="offline-features">
            <div className="offline-feature">
              <div className="offline-icon">&#128274;</div>
              <h3>Your Credits</h3>
              <p>Credits are tied to your account and can't be transferred</p>
            </div>
            <div className="offline-feature">
              <div className="offline-icon">&#8734;</div>
              <h3>Never Expire</h3>
              <p>Use your credits whenever you need them - no time limit</p>
            </div>
            <div className="offline-feature">
              <div className="offline-icon">&#128274;</div>
              <h3>Secure & Safe</h3>
              <p>Your credits are stored securely and sync when online</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">What's the difference between Single-Family and Multi-Unit?</h3>
              <p className="faq-answer">
                Single-Family credits are for homes with one dwelling unit. Multi-Unit
                credits are for buildings with 2 or more dwelling units (apartments,
                duplexes, etc.). Our AI automatically detects the plan type.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Do credits expire?</h3>
              <p className="faq-answer">
                No! Credits never expire. Purchase them when convenient and use them
                whenever you need them.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Can I use credits offline?</h3>
              <p className="faq-answer">
                Yes! That's one of the main benefits. Purchase credits when you have
                internet, then use them at job sites without connectivity. Your usage
                syncs when you reconnect.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Can I get a refund?</h3>
              <p className="faq-answer">
                Yes, unused credits can be refunded upon request. Contact our support
                team and we'll process your refund.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">What if I use the wrong credit type?</h3>
              <p className="faq-answer">
                If our AI detects your plan has more units than expected, you'll be
                prompted to pay the $30 difference to continue. No credits are wasted.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">What payment methods do you accept?</h3>
              <p className="faq-answer">
                We accept all major credit cards (Visa, Mastercard, American Express)
                through our secure payment processor Stripe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pricing-cta">
        <div className="container text-center">
          <h2 className="section-title">Ready to Save Hours on Every Estimate?</h2>
          <p className="hero-subtitle">
            Join hundreds of contractors using PlanScan to win more bids.
          </p>
          <Link to="/download" className="btn btn-primary btn-large">
            Download PlanScan
          </Link>
        </div>
      </section>
    </>
  )
}
